import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent
} from "react";
import { Check, Search } from "lucide-react";

import type { ActivityHostRenderContext } from "@/features/activity-host";
import {
  useCurriculumFeedbackAudio,
  type CurriculumAudioPlayer
} from "@/features/audio";
import { CurriculumVoiceButton } from "@/ui";

import { UNAVAILABLE_FOCUS_SOCIAL_AUDIO_PLAYER } from "./focus-social-audio";
import type {
  FocusSocialTargetSearchAction,
  FocusSocialTargetSearchRenderModel
} from "./target-search-adapter";
import "@/features/domain-slices/focus-social/focus-social-target-search.css";
import "./focus-social-player.css";

export type FocusSocialTargetSearchRendererProps =
  ActivityHostRenderContext<
    FocusSocialTargetSearchAction,
    FocusSocialTargetSearchRenderModel
  > & {
    readonly audioPlayer?: CurriculumAudioPlayer;
  };

export function FocusSocialTargetSearchRenderer({
  audioPlayer = UNAVAILABLE_FOCUS_SOCIAL_AUDIO_PLAYER,
  dispatch,
  getActivationProps,
  model,
  reducedMotion
}: FocusSocialTargetSearchRendererProps) {
  const titleId = useId();
  const objectRefs = useRef(new Map<string, HTMLButtonElement>());
  const resultHeadingRef = useRef<HTMLHeadingElement>(null);
  const shouldSyncObjectFocus = useRef(false);
  const roundAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    model.round?.audioPath === null || model.round === null
      ? null
      : {
          audioPath: model.round.audioPath,
          playbackRequest: model.round.id
        },
    model.status !== "completed"
  );
  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    model.feedback
  );

  useEffect(() => {
    if (!shouldSyncObjectFocus.current) {
      return;
    }

    shouldSyncObjectFocus.current = false;

    const nextFocusId =
      model.focusedId ??
      model.round?.objects.find(({ isAvailable }) => isAvailable)?.id ??
      null;

    if (nextFocusId !== null) {
      const nextObject = objectRefs.current.get(nextFocusId);

      nextObject?.focus({ preventScroll: true });
      nextObject?.scrollIntoView?.({
        behavior: "auto",
        block: "nearest",
        inline: "nearest"
      });
    }
  }, [model.focusedId, model.round]);

  useEffect(() => {
    if (model.status === "completed") {
      resultHeadingRef.current?.focus();
    }
  }, [model.status]);

  function sendKeyboardAction(action: FocusSocialTargetSearchAction) {
    shouldSyncObjectFocus.current = true;
    dispatch(action, "keyboard");
  }

  function focusBoundaryObject(
    objectId: string,
    boundaryObjectId: string | null
  ) {
    if (boundaryObjectId === null) {
      return;
    }

    shouldSyncObjectFocus.current = true;
    dispatch(
      { type: "focus", id: objectId, inputMethod: "keyboard" },
      "keyboard"
    );

    if (boundaryObjectId !== objectId) {
      dispatch(
        {
          type: "focus",
          id: boundaryObjectId,
          inputMethod: "keyboard"
        },
        "keyboard"
      );
    }
  }

  function handleObjectKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    objectId: string,
    firstAvailableId: string | null,
    lastAvailableId: string | null
  ) {
    if (event.repeat && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      return;
    }

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        sendKeyboardAction({ type: "move-focus", direction: "next" });
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        sendKeyboardAction({ type: "move-focus", direction: "previous" });
        break;
      case "Home":
        event.preventDefault();
        focusBoundaryObject(objectId, firstAvailableId);
        break;
      case "End":
        event.preventDefault();
        focusBoundaryObject(objectId, lastAvailableId);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        sendKeyboardAction({
          type: "select",
          id: objectId,
          inputMethod: "keyboard"
        });
        break;
    }
  }

  if (model.status === "completed" && model.result !== null) {
    const metrics = model.result.metrics;

    return (
      <section
        aria-labelledby={titleId}
        className="focus-social-search focus-social-search--result"
        data-reduced-motion={reducedMotion}
      >
        <Check aria-hidden="true" className="focus-social-search__result-icon" />
        <h2 id={titleId} ref={resultHeadingRef} tabIndex={-1}>
          藏起来的目标都找到啦
        </h2>
        <p>你按自己的节奏，把它们一个个找了出来。</p>
        <dl className="focus-social-search__metrics">
          <div>
            <dt>选择正确率</dt>
            <dd>{Math.round(metrics.correctRate * 100)}%</dd>
          </div>
          <div>
            <dt>使用提示</dt>
            <dd>{metrics.hintCount} 次</dd>
          </div>
          <div>
            <dt>重新观察</dt>
            <dd>{metrics.retryCount} 次</dd>
          </div>
          <div>
            <dt>本轮得分</dt>
            <dd>{metrics.score} 分</dd>
          </div>
        </dl>
      </section>
    );
  }

  const round = model.round;

  if (round === null) {
    return null;
  }

  const visibleObjects = round.objects.filter(
    (object) => object.isAvailable || object.isFound
  );
  const availableObjects = visibleObjects.filter(
    (object) => object.isAvailable
  );
  const firstAvailableId = availableObjects[0]?.id ?? null;
  const lastAvailableId = availableObjects.at(-1)?.id ?? null;

  return (
    <section
      aria-labelledby={titleId}
      className="focus-social-search focus-social-player-target-search"
      data-reduced-motion={reducedMotion}
    >
      <div className="focus-social-search__prompt-row">
        <div>
          <p className="focus-social-search__eyebrow">
            第 {round.number} / {round.total} 轮
          </p>
          <div className="focus-social-player__prompt-title-row">
            <h2 id={titleId}>{round.prompt}</h2>
            {round.audioPath === null ? null : (
              <CurriculumVoiceButton
                isPlaying={roundAudio.isPlaying}
                label={round.prompt}
                onPlay={roundAudio.replay}
              />
            )}
          </div>
        </div>
        <div
          aria-label={
            "本轮已找到 " +
            round.foundTargetCount +
            " 个，共 " +
            round.targetCount +
            " 个"
          }
          className="focus-social-search__round-progress"
          role="status"
        >
          <Search aria-hidden="true" />
          <span>
            {round.foundTargetCount} / {round.targetCount}
          </span>
        </div>
      </div>

      <div className="focus-social-search__feedback-row">
        <p
          aria-live="polite"
          className="focus-social-search__feedback"
          data-feedback-kind={model.feedback?.kind ?? "none"}
          role="status"
        >
          {model.feedback?.message ?? "点点陪你慢慢找。"}
        </p>
        {model.feedback === null ? null : (
          <CurriculumVoiceButton
            isPlaying={feedbackAudio.isPlaying}
            label={model.feedback.message}
            onPlay={feedbackAudio.replay}
          />
        )}
      </div>

      <div
        aria-label="可选择的物品"
        className="focus-social-search__objects"
        role="group"
      >
        {visibleObjects.map((object) => {
          const appearance = object.appearance;
          const activationProps = getActivationProps((inputMethod) => ({
            type: "select",
            id: object.id,
            inputMethod
          }));

          return (
            <button
              {...activationProps}
              aria-label={
                object.isFound
                  ? "已找到" + object.label
                  : "选择" + object.label
              }
              aria-pressed={object.isFound}
              className="focus-social-search__object"
              data-found={object.isFound}
              data-hinted={object.isHinted}
              disabled={object.isFound}
              key={object.id}
              onClickCapture={(event) => {
                shouldSyncObjectFocus.current = event.detail === 0;
              }}
              onFocus={() =>
                dispatch(
                  {
                    type: "focus",
                    id: object.id,
                    inputMethod: "keyboard"
                  },
                  "keyboard"
                )
              }
              onKeyDown={(event) =>
                handleObjectKeyDown(
                  event,
                  object.id,
                  firstAvailableId,
                  lastAvailableId
                )
              }
              ref={(node) => {
                if (node === null) {
                  objectRefs.current.delete(object.id);
                } else {
                  objectRefs.current.set(object.id, node);
                }
              }}
              tabIndex={
                object.isFocused ||
                (model.focusedId === null && object.id === firstAvailableId)
                  ? 0
                  : -1
              }
              type="button"
            >
              <span
                aria-hidden="true"
                className="focus-social-search__object-visual"
                data-accent={appearance.accent}
                data-appearance-key={appearance.appearanceKey}
                data-count={appearance.count}
                data-direction={appearance.direction}
                data-form={appearance.form}
                data-kind={appearance.kind}
                data-pattern={appearance.pattern}
                data-primary={appearance.primary}
                data-scale={appearance.scale}
              >
                <span className="focus-social-search__visual-body" />
                <span className="focus-social-search__visual-pattern" />
                <span className="focus-social-search__visual-feature" />
              </span>
              <span className="focus-social-search__object-label">
                {object.label}
              </span>
              {object.isHinted ? (
                <span className="focus-social-search__object-status">提示</span>
              ) : null}
              {object.isFound ? (
                <span className="focus-social-search__object-status">
                  <Check aria-hidden="true" />
                  已找到
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </section>
  );
}
